import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VaultKeTheme } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(120);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userEmail = userData.email || 'user@example.com';

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setIsTokenValid(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!verificationCode.trim()) {
      setErrors({ verificationCode: 'Please enter the verification code' });
      return;
    }

    if (verificationCode.trim().length !== 6) {
      setErrors({ verificationCode: 'Verification code must be 6 digits' });
      return;
    }

    if (!isTokenValid) {
      setErrors({ general: 'Verification code has expired' });
      return;
    }

    setIsLoading(true);

    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(API_ENDPOINTS.VERIFY_EMAIL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          token: verificationCode.trim(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update user data in localStorage to reflect verified status
        const updatedUserData = { ...userData, isEmailVerified: true };
        localStorage.setItem('userData', JSON.stringify(updatedUserData));

        // Redirect to user dashboard (admins can access user dashboard as well)
        navigate('/dashboard');
      } else {
        setErrors({ general: data.error || 'Verification failed' });
      }
    } catch (error) {
      console.error('Verification error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(API_ENDPOINTS.SEND_EMAIL_VERIFICATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          userId: userData.id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setTimeRemaining(120);
        setIsTokenValid(true);
        setVerificationCode('');
        setErrors({});
        alert('New verification code sent to your email');
      } else {
        setErrors({ general: data.error || 'Failed to resend code' });
      }
    } catch (error) {
      console.error('Resend error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '500px',
      width: '100%',
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    logo: {
      width: '80px',
      height: '80px',
      backgroundColor: VaultKeTheme.colors.primary,
      borderRadius: '50%',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.lg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '32px',
      fontWeight: 'bold',
      color: VaultKeTheme.colors.background,
    },
    logoText: {
      fontSize: VaultKeTheme.typography.h1.fontSize,
      fontWeight: VaultKeTheme.typography.h1.fontWeight,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    card: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.xl,
      padding: VaultKeTheme.spacing.xxl,
      boxShadow: VaultKeTheme.shadows.lg,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.5',
    },
    countdownCard: {
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    countdownContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: VaultKeTheme.spacing.sm,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    countdownIcon: {
      fontSize: '28px',
    },
    countdownText: {
      fontSize: '36px',
      fontWeight: 'bold',
      fontFamily: 'monospace',
      color: VaultKeTheme.colors.primary,
    },
    countdownLabel: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
    expiredCard: {
      backgroundColor: VaultKeTheme.colors.error + '15',
      border: `1px solid ${VaultKeTheme.colors.error}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    expiredText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.error,
      fontWeight: '600',
    },
    formGroup: {
      marginBottom: VaultKeTheme.spacing.xl,
    },
    label: {
      display: 'block',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      fontWeight: '600',
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
      textAlign: 'center',
    },
    input: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      border: `2px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      color: VaultKeTheme.colors.text,
      fontSize: '24px',
      fontWeight: 'bold',
      fontFamily: 'monospace',
      letterSpacing: '8px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
    inputError: {
      borderColor: VaultKeTheme.colors.error,
    },
    errorText: {
      fontSize: VaultKeTheme.typography.caption.fontSize,
      color: VaultKeTheme.colors.error,
      marginTop: VaultKeTheme.spacing.xs,
      textAlign: 'center',
    },
    button: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: VaultKeTheme.colors.primary,
      color: VaultKeTheme.colors.background,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.lg,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    secondaryButton: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: 'transparent',
      color: VaultKeTheme.colors.primary,
      border: `1px solid ${VaultKeTheme.colors.primary}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.sm,
    },
    resendText: {
      textAlign: 'center',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    resendLink: {
      color: VaultKeTheme.colors.primary,
      textDecoration: 'none',
      fontWeight: '600',
      cursor: 'pointer',
    },
  };

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>VK</div>
          <h1 style={styles.logoText}>VaultKe</h1>
        </div>

        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Verify Your Email</h2>
            <p style={styles.subtitle}>
              We've sent a 6-digit code to<br/>
              <strong>{userEmail}</strong>
            </p>
          </div>

          {isTokenValid ? (
            <div style={styles.countdownCard}>
              <div style={styles.countdownContainer}>
                <span style={styles.countdownIcon}>⏱️</span>
                <span style={styles.countdownText}>{formatTime(timeRemaining)}</span>
              </div>
              <div style={styles.countdownLabel}>Time remaining to verify your email</div>
            </div>
          ) : (
            <div style={styles.expiredCard}>
              <div style={styles.expiredText}>Verification code has expired</div>
            </div>
          )}

          {isTokenValid && (
            <form onSubmit={handleVerify}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Enter 6-digit code</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    ...styles.input,
                    ...(errors.verificationCode ? styles.inputError : {}),
                  }}
                />
                {errors.verificationCode && <div style={styles.errorText}>{errors.verificationCode}</div>}
                {errors.general && (
                  <div style={{
                    ...styles.errorText,
                    textAlign: 'center',
                    marginTop: VaultKeTheme.spacing.md,
                    padding: VaultKeTheme.spacing.sm,
                    backgroundColor: VaultKeTheme.colors.error + '20',
                    borderRadius: VaultKeTheme.borderRadius.md,
                    border: `1px solid ${VaultKeTheme.colors.error}`,
                  }}>
                    {errors.general}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                style={{
                  ...styles.button,
                  ...(isLoading || verificationCode.length !== 6 ? styles.buttonDisabled : {}),
                }}
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </button>

              <div style={styles.resendText}>
                Didn't receive the code?{' '}
                <span style={styles.resendLink} onClick={handleResendCode}>
                  Resend
                </span>
              </div>
            </form>
          )}

          {!isTokenValid && (
            <button onClick={handleResendCode} style={styles.secondaryButton}>
              Send New Code
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;